import { useState, useEffect } from "react";
import { isApiConnected } from "@/lib/apiClient";
import { listLiveCourses, matchLiveCourseByName, realCategoryForSlug, type LiveCourseInfo } from "@/services/courseService";
import { courses as mockCourses } from "@/mocks/siteData";

export interface Course {
  id?: string;
  slug: string;
  name: string;
  category: string;
  difficulty: string;
  duration: string;
  price: number;
  seats: number;
  image: string;
  shortDescription: string;
  fullDescription: string;
  itinerary: Array<{ day: number; title: string; description: string }>;
  eligibility: string[];
  equipment: string[];
  reviews: number;
  rating: number;
  location: string;
  address?: string;
  mapLinks?: string[];
  subCourses?: Array<{ name: string; duration: string }>;
  highlightNote?: string;
  highlights?: string[];
}

function mergeCourse(mock: Course, live?: LiveCourseInfo): Course {
  // Real Category Name (e.g. "Mountaineering") replaces the mock's broad
  // land/aqua/aero grouping, looked up by slug (reliable) rather than the
  // live-data name match (fragile — see courseService.realCategoryForSlug's
  // comment) so every course gets a correct category, not just the ~7 that
  // happen to fuzzy-match a live row.
  const category = realCategoryForSlug(mock.slug) || mock.category;
  if (!live) return { ...mock, category };
  return {
    ...mock,
    category,
    id: live.id || mock.id,
    price: live.fees || mock.price,
    seats: live.availableSeats || live.seats || mock.seats,
  };
}

export function useCourses(): { courses: Course[]; loading: boolean; error: string | null } {
  const [courses, setCourses] = useState<Course[]>(mockCourses as unknown as Course[]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fallbackData = mockCourses as unknown as Course[];

    if (!isApiConnected()) {
      setCourses(fallbackData);
      setLoading(false);
      return;
    }

    let cancelled = false;

    listLiveCourses()
      .then((live) => {
        if (cancelled) return;
        setCourses(fallbackData.map((mock) => mergeCourse(mock, matchLiveCourseByName(live, mock.name))));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load courses");
        setCourses(fallbackData);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { courses, loading, error };
}

export function useCourse(slug: string): { course: Course | null; loading: boolean; error: string | null } {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fallback = mockCourses.find((c) => c.slug === slug) as unknown as Course | null;

    if (!slug) {
      setLoading(false);
      return;
    }

    if (!fallback) {
      setError("Course not found");
      setLoading(false);
      return;
    }

    if (!isApiConnected()) {
      setCourse(fallback);
      setLoading(false);
      return;
    }

    let cancelled = false;

    listLiveCourses()
      .then((live) => {
        if (cancelled) return;
        setCourse(mergeCourse(fallback, matchLiveCourseByName(live, fallback.name)));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load course");
        setCourse(fallback);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { course, loading, error };
}
